import React from 'react';
import Blogroll from './blocks/blogroll';
import CommunityBanner from './blocks/community-banner';
import Faqs from './blocks/faqs/faqs';
import FaqsGrid from './blocks/faqs/faqs-grid';
import MediaContentFaqs from './blocks/faqs/media-content-faqs';
import FranchiseDisclosure from './blocks/franchise-disclosure';
import Gallery from './blocks/gallery';
import IconBullets from './blocks/icon-bullets';
import IngredientsDisclosure from './blocks/ingredients-disclosure';
import MediaContent from './blocks/media-content';
import MultiBlogroll from './blocks/multi-blogroll';
import RichTextBlock from './blocks/richtext-block';
import ServicesDetail from './blocks/services-detail';
import ServicesSlider from './blocks/services-slider';
import Slider from './blocks/slider';
import SplitTextBanner from './blocks/split-text-banner';
import SubnavStripe from './blocks/subnav-stripe';
import TeamMemberGrid from './blocks/team-members';
import Testimonial from './blocks/testimonial';
import TextBanner from './blocks/text-banner';
import Form from './blocks/form';
import Anchor from './blocks/anchor';
import ServicesWeProvide from './blocks/services-we-provide';
import ServiceArea from './blocks/service-area';
import FranchiseAboutInfo from './blocks/franchise-about-info';
import FranchiseCollapsibleAbout from './blocks/franchise-collapsible-about';
import FranchiseTeamMembers from './blocks/franchise-team-members';
import { useDebug } from './primitives/debug/debug-provider';
import DebugPill from './primitives/debug/debug-pill';
import EmptyBlock from './blocks/empty-block';
import FranchiseFormHero from './blocks/franchise-form-hero';
import FranchiseFormCta from './blocks/franchise-form-cta';
import Hero from './blocks/hero';
import AboutFranchiseHero from './blocks/about-franchise-hero';
import FranchiseContactHero from './blocks/franchise-contact-hero';
import ErrorBoundary from './utils/error-boundary';
import PropsBrowser from './primitives/debug/props-browser';
import { ImagePriorityProvider } from '../contexts/image-priority-context';
/*
* Context for retaining render depth
* */
export const BlockRendererContext = React.createContext(-1);

/* eslint-disable-next-line */
type Block = React.ComponentType<any>;

const getBlock = (name: string): Block|null => {
    switch (name) {
        case 'FranchiseFormCta':
            return FranchiseFormCta;
        case 'FranchiseFormHero':
            return FranchiseFormHero;
        case 'Blogroll':
            return Blogroll;
        case 'CommunityBanner':
            return CommunityBanner;
        case 'Faqs':
            return Faqs;
        case 'FranchiseDisclosure':
            return FranchiseDisclosure;
        case 'Gallery':
            return Gallery;
        case 'Hero':
            return Hero;
        case 'IconBullets':
            return IconBullets;
        case 'IngredientsDisclosure':
            return IngredientsDisclosure;
        case 'MediaContent':
            return MediaContent;
        case 'MediacontentFaqs':
            return MediaContentFaqs;
        case 'FaqsGrid':
            return FaqsGrid;
        case 'MultiBlogroll':
            return MultiBlogroll;
        case 'RichtextBlock':
            return RichTextBlock;
        case 'ServicesDetail':
            return ServicesDetail;
        case 'ServicesSlider':
            return ServicesSlider;
        case 'Slider':
            return Slider;
        case 'SplitTextBanner':
            return SplitTextBanner;
        case 'SubnavStripe':
            return SubnavStripe;
        case 'TeamMemberGrid':
            return TeamMemberGrid;
        case 'Testimonial':
            return Testimonial;
        case 'TextBanner':
            return TextBanner;
        case 'Form':
            return Form;
        case 'Anchor':
            return Anchor;
        case 'ServicesWeProvide':
            return ServicesWeProvide;
        case 'ServiceArea':
            return ServiceArea;
        case 'AboutFranchiseHero':
            return AboutFranchiseHero;
        case 'FranchiseDetails':
            return FranchiseAboutInfo;
        case 'FranchiseContactHero':
            return FranchiseContactHero;
        case 'FranchiseCollapsibleAbout':
            return FranchiseCollapsibleAbout;
        case 'FranchiseAboutInfo':
            return FranchiseAboutInfo;
        case 'FranchiseTeamMembers':
            return FranchiseTeamMembers;
        default:
            return null;
    }
};

export interface BlockRendererProps {
    data?: (Queries.CsUnionForPageBuilderToSections & { __typename?: string|null })[]
}

const BlockRenderer = ({ data }: BlockRendererProps): JSX.Element => {
    const existingRenderContextDepth = React.useContext(BlockRendererContext);
    const { showBlockNames } = useDebug();

    return (
        <BlockRendererContext.Provider value={existingRenderContextDepth + 1}>
            {data && data.map((blockData, index) => {
                // eslint-disable-next-line no-underscore-dangle
                if (!('__typename' in blockData) || !blockData?.__typename) {
                    if (process.env.NODE_ENV === 'development') {
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <EmptyBlock key={`empty-block-${index}`}>
                                Unable to find any block for data point.
                            </EmptyBlock>
                        );
                    }
                    return null;
                }

                // eslint-disable-next-line no-underscore-dangle
                const typeName = blockData?.__typename?.replace('Cs', '').replace('Group', '');

                const Component = getBlock(typeName);

                if (!Component) {
                    if (process.env.NODE_ENV === 'development') {
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <EmptyBlock key={`missing-${typeName}-${index}`}>
                                Unable to find block
                                {' '}
                                {typeName}
                                .
                            </EmptyBlock>
                        );
                    }
                    return null;
                }

                // Eagerly load images only in the hero block (index 0) to avoid bandwidth competition
                // that would slow down LCP. This is computed synchronously during render so it's available during SSR
                const isEarlyBlock = index === 0;

                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={`${typeName}-block-${index}`}>
                        {showBlockNames && (
                            <div css={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: '0.3s',
                                zIndex: 10000,
                            }}
                            >
                                <DebugPill>
                                    {typeName}
                                </DebugPill>
                                <DebugPill>
                                    <PropsBrowser {...blockData} />
                                </DebugPill>
                            </div>
                        )}
                        <ErrorBoundary>
                            <ImagePriorityProvider isEarlyBlock={isEarlyBlock}>
                                <Component {...blockData} />
                            </ImagePriorityProvider>
                        </ErrorBoundary>
                    </React.Fragment>
                );
            })}
        </BlockRendererContext.Provider>
    );
};

export default BlockRenderer;
